import React, { useState, useEffect } from "react";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Chart } from "primereact/chart";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import html2canvas from "html2canvas";
import { Badge } from "primereact/badge";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Accordion, AccordionTab } from "primereact/accordion";
import * as Constants from "../../../constants";
import { t } from "../../../content";
import { useLocale } from "../../../locale";
import "./AnalyticsDataTable.scss";

const SCORE_COLORS = [
  { color: "#00B0A0", points_list: [1, 2, 3] },
  { color: "#FFBB31", points_list: [4, 5, 6] },
  { color: "#8049B0", points_list: [7, 8, 9] },
];

const AnalyticsDataTable = (props) => {
  const { locale } = useLocale();
  const MAX_WORDS_PER_LABEL = 2;
  const tabKey = props?.tabActiveKey?.toString();

  const [radarLabelDesc, setRadarLabelDesc] = useState("");
  const [accordionArray, setAccordionArray] = useState([]);
  const [modal, setModal] = useState(false);
  const [report, setReport] = useState();
  const [highScales, setHighScales] = useState();
  const [lowScales, setLowScales] = useState();
  const [value, setValue] = useState();
  const [selectedUser, setSelectedusers] = useState([]);
  const [reportName, setReportName] = useState("");
  const [chartData, setChartData] = useState([]);
  const [columns, setcolumns] = useState([]);
  const [colors, setColors] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sorted, setSorted] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  function handleScreenshotClick() {
    const modal = document.querySelector("#ss-modal_content");
    const modalOverflow = modal.style.overflow;

    // Set overflow to visible to show the whole modal content
    modal.style.overflow = "visible";
    // Hide the element to ignore

    html2canvas(modal).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `${reportName}_RadarChart.png`;
      link.href = imgData;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });

    // Reset overflow to its original value
    modal.style.overflow = modalOverflow;
  }

  function splitCamelCase(property) {
    return property.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  }

  function convertHyphenatedToSpacedWords(input) {
    return input?.replace(/-/g, " ").toLowerCase();
  }

  function capitalizeFirstLowercaseRest(str) {
    if (str && typeof str === "string") {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    } else {
      return "";
    }
  }

  useEffect(() => {
    let labelLower = convertHyphenatedToSpacedWords(value);

    if (tabKey !== Constants.TAB_COMPETENCIES_KEY && tabKey !== Constants.TAB_BEHAVIOURS_KEY) {
      for (const propertyName in props?.scaleDescription) {
        const originalProperty = propertyName;
        const splitProperty = splitCamelCase(originalProperty);
        const lowerCaseProperty = splitProperty.toLowerCase();

        // Trim the strings to remove any leading or trailing spaces, accidentally added in Contentful or backend API
        if (lowerCaseProperty?.trim() === labelLower?.trim()) {
          setHighScales(props?.scaleDescription[propertyName]?.high);
          setLowScales(props?.scaleDescription[propertyName]?.low);
          setAccordionArray(
            Array.isArray(props?.scaleDescription[propertyName]?.properties)
              ? props.scaleDescription[propertyName].properties
              : []
          );
          break;
        } else {
          // If the label is not found, reset the high and low scales and the accordion array to avoid showing the previous values
          setHighScales(null);
          setLowScales(null);
          setAccordionArray([]);
        }
      }
    } else {
      for (const propertyName in props?.scaleDescription) {
        const originalProperty = propertyName;
        const splitProperty = splitCamelCase(originalProperty);
        const lowerCaseProperty = splitProperty.toLowerCase();

        // Trim the strings to remove any leading or trailing spaces, accidentally added in Contentful or backend API
        if (lowerCaseProperty?.trim() === labelLower?.trim()) {
          setAccordionArray(
            Array.isArray(props?.scaleDescription[propertyName]?.properties)
              ? props.scaleDescription[propertyName].properties
              : []
          );
          setRadarLabelDesc(props?.scaleDescription[propertyName]?.title || "");
          break;
        } else {
          // If the label is not found, reset the high and low scales and the accordion array to avoid showing the previous values
          setAccordionArray([]);
          setRadarLabelDesc("");
        }
      }
    }
  }, [value, tabKey, props?.scaleDescription, locale]);

  const getLabelIndex = (x, y, pointLabelsArray) => {
    let index = -1;
    for (let i = 0; i < pointLabelsArray.length; i++) {
      const { top, right, bottom, left } = pointLabelsArray[i];
      if (x >= left && x <= right && y >= top && y <= bottom) {
        index = i;
        break;
      }
    }
    return index;
  };
  const [lightOptions] = useState({
    maintainAspectRatio: false, // Set to false to customize the aspect ratio
    onClick: (evt, activeEls, chart) => {
      const { x, y } = evt;
      let index = -1;

      for (let i = 0; i < chart.scales.r._pointLabelItems.length; i++) {
        const { bottom, top, left, right } = chart.scales.r._pointLabelItems[i];

        if (x >= left && x <= right && y >= top && y <= bottom) {
          index = i;
          break;
        }
      }

      if (index === -1) {
        return;
      }

      let clickedLabel = chart.scales.r._pointLabels[index];
      clickedLabel = clickedLabel?.join(" ");
      setValue(clickedLabel);
      setModal(true);
    },
    onHover: ({ x, y }, activeHover, chart) => {
      const { canvas } = chart;
      let index = getLabelIndex(x, y, chart.scales.r._pointLabelItems);
      if (index === -1) {
        canvas.style.cursor = "default";
        chart.update();
      } else {
        canvas.style.cursor = "pointer";
      }
    },
    plugins: {
      legend: {
        labels: {
          color: "#495057",
        },
      },
    },

    scales: {
      r: {
        max: 9,
        min: 0,
        ticks: {
          stepSize: 1,
        },
        pointLabels: {
          color: "#495057",
          font: 24,
        },
        grid: {
          color: "#ebedef",
        },
        angleLines: {
          color: "#ebedef",
        },
      },
    },
  });

  useEffect(() => {
    if (props?.dataTableList) calculateSAvergae();
  }, [props?.dataTableList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tabKey === Constants.TAB_COMPETENCIES_KEY) {
      setReportName(t('analytics.table.workStylesFile'));
      setReport(t('analytics.table.workStylesReport'));
    } else if (tabKey === Constants.TAB_BEHAVIOURS_KEY) {
      setReportName(t('analytics.table.behavioursFile'));
      setReport(t('analytics.table.behavioursReport'));
    } else if (tabKey === Constants.TAB_MOTIVATIONS_KEY) {
      setReportName(t('analytics.table.motivationsFile'));
      setReport(t('analytics.table.motivationsReport'));
    } else if (tabKey === Constants.TAB_TEAM_STRENGTHS_KEY) {
      setReportName(t('analytics.table.teamStrengthsFile'));
      setReport(t('analytics.table.teamStrengthsReport'));
    } else if (tabKey === Constants.TAB_ADEPT_ASPECTS_KEY) {
      setReportName(t('analytics.table.adeptFile'));
      setReport(t('analytics.table.adeptReport'));
    }
  }, [tabKey, locale]);

  useEffect(() => {
    if (props.chartDialog) {
      customDataForChart();
    }
    if (!filters.length && props?.allCol) {
      setIntialFilters();
    }
  }, [props?.chartDialog, props?.allCol]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedusers([]);
    props.selectedRow(null);
  }, [props?.dataTableList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props?.allCol) {
      calculateSAvergae();
    }
  }, [props?.allCol]); // eslint-disable-line react-hooks/exhaustive-deps

  const setIntialFilters = () => {
    const allCol = props?.allCol;
    let obj = {};
    allCol?.forEach((col) => {
      obj[`${col.column_name}`] = {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
      };
    });
    setFilters(obj);
  };

  const calculateSAvergae = () => {
    let avgArray = [];
    if (props?.allCol?.length && props?.dataTableList?.length) {
      props?.allCol.forEach((col) => {
        const sum = props.dataTableList
          .map((item) => item[`${col.column_name}`])
          .reduce((total, value) => total + value, 0);
        const average = sum / props.dataTableList.length;
        avgArray.push({ ...col, avg: average.toFixed(1) });
      });
      avgArray.sort((a, b) => b.avg - a.avg);
      setcolumns(avgArray);
    }
  };

  useEffect(() => {
    if (colors.length === 0) {
      setColors(SCORE_COLORS);
    }
  }, [colors]);

  const customDataForChart = () => {
    const labels = [];
    const dataset = [];
    let chartDataObj = {};
    let userName = "";

    let selAvgArray = [];
    if (props?.allCol?.length && selectedUser?.length) {
      props?.allCol.forEach((col) => {
        const summed = selectedUser
          .map((item) => item[`${col.column_name}`])
          .reduce((total, value) => total + value, 0);
        const selAverage = summed / selectedUser.length;
        selAvgArray.push({ ...col, avg: selAverage.toFixed(1) });
      });
    }

    props?.allCol?.forEach((col) => {
      selAvgArray.forEach((obj) => {
        if (col.header_name === obj.header_name) {
          labels.push(col.header_name);
        }
      });
    });
    let customColorArray = [
      { color: "#7900FF" },
      { color: "#9F6400" },
      { color: "#C13FA8" },
      { color: "#FFCC00" },
      { color: "#150941" },
      { color: "#00B2A0" },
      { color: "#AAAD00" },
      { color: "#F87000" },
      { color: "#FF66C4" },
      { color: "#2ECBDC" },
      { color: "#8D0404" },
      { color: "#004AAD" },
      { color: "#545454" },
      { color: "#548CFF" },
      { color: "#00BF63" },
    ];

    selectedUser.forEach((user, index) => {
      let data = [];
      let color;
      let userName = user.first_name + " " + user.last_name;
      const keys = Object.keys(user);
      for (let j = 0; j < keys.length; j++) {
        if (
          typeof user[`${keys[j]}`] === "number" &&
          keys[j] !== "user_id" &&
          props?.allCol?.some((val) => val.column_name === keys[j])
        ) {
          data.push(user[`${keys[j]}`]);
        }
      }

      if (index < 15) {
        color = customColorArray[index].color;
      } else {
        color = "#" + Math.floor(Math.random() * 16777215).toString(16);
        color = color.replace(/[^0-9A-F]/gi, ""); // remove any non-hexadecimal characters
        if (color.length < 6) {
          color += "0".repeat(6 - color.length); // add trailing zeros if necessary
        }
        color = "#" + color;
      }

      dataset.push({
        label: userName,
        data: data,
        borderColor: color,
        pointHoverBackgroundColor: "#fff",
      });
    });
    chartDataObj = {
      userName: userName,
      labels: convertLabelsToArrayOfArrayOfLabels(labels).map((obj) => {
        return obj;
      }),
      datasets: dataset,
    };
    setChartData(chartDataObj);
  };

  const convertLabelsToArrayOfArrayOfLabels = (labelsArray) => {
    return labelsArray.map((label) => {
      if (typeof label === "string") {
        const words = label.split(" ");

        if (words.length <= MAX_WORDS_PER_LABEL) {
          return [label];
        }

        const splitLabels = [];

        for (let i = 0; i < words.length; i += MAX_WORDS_PER_LABEL) {
          splitLabels.push(words.slice(i, i + MAX_WORDS_PER_LABEL).join(" "));
        }

        return splitLabels;
      } else {
        return label;
      }
    });
  };

  const onSlection = (e, data) => {
    data.isSelected = e.target.checked;
    if (data.isSelected) {
      props.dataTableList.forEach((listVal) => {
        if (listVal.user_id === data.user_id) {
          setSelectedusers([...selectedUser, listVal]);
          props.selectedRow([...selectedUser, listVal]);
        }
      });
    } else {
      onUnselect(data);
    }
  };

  const onUnselect = (data) => {
    let selectedRows = [...selectedUser];
    if (selectedRows.length > 0) {
      const index = selectedRows.findIndex(
        (val) => val.user_id === data.user_id
      );
      selectedRows.splice(index, 1);
      setSelectedusers([...selectedRows]);
      if (!selectedRows.length) {
        props.selectedRow(null);
      } else {
        props.selectedRow([...selectedRows]);
      }
    }
  };

  const statusBodyTemplate = (rowData, column) => {
    const field = column.field;
    let clr = "";
    colors &&
      colors?.forEach((val) => {
        if (val.points_list.includes(rowData[`${field}`])) {
          clr = val.color;
        }
      });
    return (
      <Badge
        value={rowData[`${field}`]}
        size="large"
        severity="success"
        style={{ background: `${clr}`, color: "white" }}
      ></Badge>
    );
  };

  const firstNameBody = (rowData, col) => {
    return (
      <>
        <label
          className="custom-checkbox"
          tabIndex="0"
          aria-label={t('common.labels.name')}
        >
          <input
            type="checkbox"
            checked={
              selectAll ||
              selectedUser.some((user) => user.user_id === rowData.user_id)
            }
            onChange={(e) => onSlection(e, rowData, !rowData.isSelected)}
          />
          <span className="checkmark"></span>
        </label>
        <span
          className="image-text"
          id="pointer-span"
          onClick={(e) => handleRowClick(e, rowData)}
        >
          {rowData.first_name + " " + rowData.last_name}
        </span>
        <span className="icon-span">ⓘ</span>
      </>
    );
  };

  const headerBtnTemplate = () => {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <span>{report}</span>
      </div>
    );
  };

  const footerTemplate = (data) => {
    let clr = "";
    colors?.forEach((val) => {
      if (val?.points_list?.includes(Math.round(data.avg))) {
        clr = val.color;
      }
    });
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Badge
          value={data.avg}
          size="xlarge"
          severity="success"
          style={{
            background: `${clr}`,
            color: "white",
            lineHeight: "3rem !important",
          }}
        ></Badge>
      </div>
    );
  };

  const headerClassTemplate = (header, toolTip) => {
    return (
      <>
        <Tooltip target=".tool-tip" position="top" />
        <p
          style={{ cursor: "pointer" }}
          className="tool-tip"
          placeholder={header}
          data-pr-tooltip={toolTip}
        >
          {header}{" "}
        </p>
      </>
    );
  };

  const UpdateAverage = (data) => {
    let FilteredavgArray = [];
    if (data?.length) {
      props?.allCol.forEach((col) => {
        const sum = data
          .map((item) => item[`${col.column_name}`])
          .reduce((total, value) => total + value, 0);
        const average = sum / data.length;
        FilteredavgArray.push({ ...col, avg: average.toFixed(1) });
      });
      FilteredavgArray.sort((a, b) => b.avg - a.avg);
      setcolumns(FilteredavgArray);
    }
  };

  const handleRowClick = (e, data) => {
    props?.setRowData(data);
  };

  const sortColumn = (val) => {
    setSorted(val);
    if (val) {
      columns.sort((a, b) => b.avg - a.avg);
    } else {
      columns.sort((a, b) => a.avg - b.avg);
    }
  };
  const toggleSelectAll = () => {
    if (selectedUser.length === props.dataTableList.length) {
      // If all rows are selected, deselect all.
      setSelectedusers([]);
      props.selectedRow([]);
      setSelectAll(false);
    } else {
      // If not all rows are selected, select all.
      setSelectedusers([...props.dataTableList]);
      props.selectedRow([...props.dataTableList]);
      setSelectAll(true);
    }
  };

  const averageHeading = () => {
    return (
      <>
        <span
          style={{
            cursor: "pointer",
            display: "flex",
            height: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span className="average-margin">{t('common.scale.average')}</span>
          <button
            className="sorting-button"
            onClick={() => sortColumn(!sorted)}
          >
            <i
              style={{ scale: "1.5" }}
              className={`${
                sorted
                  ? "pi pi-sort-amount-up rotate-270"
                  : "pi pi-sort-amount-down-alt rotate-270"
              }`}
            />
          </button>
        </span>
      </>
    );
  };

  let footerGroup = columns.length ? (
    <ColumnGroup>
      <Row>
        <Column
          colSpan={2}
          footer={averageHeading}
          style={{
            dispaly: "flex !important",
            flexGrow: 1,
            flexBasis: "240px",
            justifyContent: "flex-start",
          }}
          frozen
        />
        {columns &&
          columns.map((column, index) => {
            return (
              <Column
                key={index}
                footer={footerTemplate(column)}
                style={{
                  flexGrow: 1,
                  flexBasis: "220px",
                  justifyContent: "center",
                }}
              />
            );
          })}
      </Row>
    </ColumnGroup>
  ) : null;

  const footerForDownloadIcon = () => {
    return (
      <button className="green-download" onClick={handleScreenshotClick}>
        <i className="pi pi-download custom-dimensions"></i>
      </button>
    );
  };

  return (
    <>
      <DataTable
        key={locale}
        value={props.dataTableList}
        scrollable
        scrollHeight="600px"
        responsiveLayout="scroll"
        scrollDirection="both"
        showGridlines
        filterDisplay="menu"
        filters={filters}
        paginator
        rows={20}
        className="mt-3"
        footerColumnGroup={footerGroup}
        onValueChange={UpdateAverage}
      >
        <Column
          key="selectAllAndName"
          header={() => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="p-checkbox">
                <label
                  className="custom-checkbox"
                  tabIndex="0"
                  aria-label={t('common.labels.name')}
                >
                  <input
                    type="checkbox"
                    className="p-checkbox-input"
                    checked={selectedUser.length === props.dataTableList.length}
                    onChange={toggleSelectAll}
                  />
                  <span className="checkmark"></span>
                </label>
              </div>
              <span style={{ marginLeft: "8px" }}>{t('common.labels.name')}</span>
            </div>
          )}
          style={{
            width: "15rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
          body={firstNameBody}
          frozen
        />
        {columns &&
          columns.map((column, index) => {
            return (
              <Column
                key={index}
                field={column.column_name}
                header={headerClassTemplate(
                  column.header_name,
                  column.tool_tip ? column.tool_tip : column.header_name
                )}
                style={{
                  flexGrow: 1,
                  flexBasis: "220px",
                  justifyContent: "center",
                }}
                filter
                dataType="numeric"
                body={statusBodyTemplate}
              ></Column>
            );
          })}
      </DataTable>

      {/* </div> */}
      <Dialog
        id="ss-modal"
        header={headerBtnTemplate}
        visible={props.chartDialog}
        style={{ width: "1400px" }}
        modal
        className="p-fluid"
        onHide={() => props.onChartHide()}
        footer={footerForDownloadIcon}
      >
        <div className="grid-items">
          {props.scaleDescription && (
            <Dialog
              className="label-dialog"
              headerClassName="label-dialog-header"
              contentClassName="label-dialog-content"
              visible={modal}
              style={{ width: "400px", borderRadius: "50px" }}
              onHide={() => setModal(false)}
            >
              {tabKey !== Constants.TAB_COMPETENCIES_KEY &&
              tabKey !== Constants.TAB_BEHAVIOURS_KEY ?
              (
                <>
                  <div className="padding-div">
                    <p className="">
                      {value}{" "}
                      <span style={{ color: "#000000", fontWeight: "700" }}>
                        {t('analytics.table.isAboveAverage')}
                      </span>
                    </p>
                    <ul className="list-margin">
                      {highScales?.map((obj, index) => {
                        return (
                          <li className="list-font" key={index}>
                            <span className="checkmark">&#x2713;</span>{" "}
                            <span>{highScales[index]}</span>{" "}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="padding-div">
                    <p className="">
                      {value}{" "}
                      <span style={{ color: "#000000", fontWeight: "700" }}>
                        {t('analytics.table.isBelowAverage')}
                      </span>
                    </p>
                    <ul className="list-margin">
                      {lowScales?.map((obj, index) => {
                        return (
                          <li className="list-font" key={index}>
                            <span className="crossmark">&#x2715;</span>{" "}
                            <span>{lowScales[index]}</span>{" "}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  {accordionArray.length > 0 && (
                    <Accordion>
                      {accordionArray.map(
                        (item, index) =>
                          item.content?.length > 0 && (
                            <AccordionTab
                              key={index}
                              header={capitalizeFirstLowercaseRest(item.title)}
                              headerClassName="accordian-header-class"
                            >
                              {/* Content for each accordion tab */}
                              <div>
                                <ul>
                                  {item.content.map(
                                    (contentItem, contentIndex) => (
                                      <li
                                        className="list-style-disc"
                                        key={contentIndex}
                                      >
                                        {contentItem}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            </AccordionTab>
                          )
                      )}
                    </Accordion>
                  )}
                </>
              ) : (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <div style={{ marginBottom: "20px", textAlign: "center" }}>
                      <span
                        style={{
                          fontStyle: "normal",
                          fontWeight: "bold",
                          fontSize: "18px",
                        }}
                      >
                        {value}
                      </span>
                    </div>
                    <div style={{ margin: "0 10px 15px 10px" }}>
                      <span style={{ fontStyle: "italic" }}>
                        {radarLabelDesc}
                      </span>
                    </div>
                    {accordionArray.length > 0 && (
                      <Accordion>
                        {accordionArray.map(
                          (item, index) =>
                            item.content?.length > 0 && (
                              <AccordionTab
                                key={index}
                                header={capitalizeFirstLowercaseRest(item.title)}
                                headerClassName="accordian-header-class"
                              >
                                <div>
                                  <ul>
                                    {item.content.map(
                                      (contentItem, contentIndex) => (
                                        <li
                                          className="list-style-disc"
                                          key={contentIndex}
                                        >
                                          {contentItem}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              </AccordionTab>
                            )
                        )}
                      </Accordion>
                    )}
                  </div>
                </>
              )}
            </Dialog>
          )}
          <div style={{ gridColumn: 2 }}>
            <Chart
              type="radar"
              width="700px"
              height="700px"
              data={chartData}
              options={lightOptions}
              style={{ position: "relative" }}
            />
          </div>
          <div className="enclosing-div">
            <div className="top-div">
              <div className="instructions-div">
                <p className="margin-class-head">{t('analytics.table.howToReadChart')}</p>
                <p className="margin-class-font">{t('analytics.table.aboveAverageRange')}</p>
                <p className="margin-class-font">{t('analytics.table.belowAverageRange')}</p>
                <p className="margin-class-head">
                  {t('analytics.table.howToGetInsights')}
                </p>
                <p className="margin-class-font">
                  {t('analytics.table.clickStrengths')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AnalyticsDataTable;
